import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  Alert,
} from "react-native";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { MaterialIcons, Feather } from "@expo/vector-icons";
import { format } from "date-fns";

import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { supabase } from "../lib/supabase";
import type { RootStackParamList } from "../types/navigation";
import type { BuyerOrder } from "./BuyerOrderDetailsScreen";
import { formatPrice } from "../utils/format";
import { DeliveryHelpers } from "../config/deliveryConfig";

type Nav = NativeStackNavigationProp<RootStackParamList>;
type RouteP = RouteProp<
  { params: { orderId?: string; order?: BuyerOrder } },
  "params"
>;

const money = (n?: number | null) =>
  typeof n === "number" && !Number.isNaN(n) ? formatPrice(n) : "—";

function toDisplayLine(line: any) {
  if (line?.title || line?.unit_price) {
    return {
      title: line.title ?? "Item",
      qty: Number(line.quantity ?? 1),
      price: Number(line.unit_price ?? 0),
    };
  }
  const p = line?.product ?? {};
  return {
    title: p.title ?? "Item",
    qty: Number(line?.quantity ?? 1),
    price: Number(p.price ?? 0),
  };
}

export default function ReceiptScreen() {
  const colors = useColors();
  const s = createStyles(colors);
  const navigation = useNavigation<Nav>();
  const route = useRoute<RouteP>();
  const [loading, setLoading] = React.useState(false);
  const [order, setOrder] = React.useState<BuyerOrder | null>(
    route.params?.order ?? null
  );

  React.useEffect(() => {
    (async () => {
      if (order || !route.params?.orderId) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("orders")
          .select("*")
          .eq("id", route.params.orderId)
          .maybeSingle();
        if (error) throw error;
        if (data) setOrder(data as BuyerOrder);
      } catch (e: any) {
        Alert.alert("Error", e?.message || "Failed to load receipt");
      } finally {
        setLoading(false);
      }
    })();
  }, [route.params?.orderId]);

  if (loading || !order) {
    return (
      <View style={s.center}>
        <ActivityIndicator color={colors.primary} />
        <Text style={s.muted}>Loading receipt…</Text>
      </View>
    );
  }

  const created = order.created_at ? new Date(order.created_at) : new Date();
  const lines = Array.isArray(order.items)
    ? order.items.map(toDisplayLine)
    : [];
  const breakdown = order.payment_details?.breakdown || {};
  const subtotal =
    typeof breakdown.items_subtotal === "number"
      ? breakdown.items_subtotal
      : undefined;
  const delivery =
    typeof order.delivery_fee === "number"
      ? order.delivery_fee
      : typeof breakdown.delivery_total === "number"
      ? breakdown.delivery_total
      : undefined;
  const platformFee =
    typeof breakdown.platform_fee === "number"
      ? breakdown.platform_fee
      : undefined;

  const stripeMeta = order.payment_details?.stripe || {};
  // brand/last4 опционально, если передаёшь их в payment_details.stripe
  const brand = (stripeMeta.brand as string) || undefined;
  const last4 = (stripeMeta.last4 as string) || undefined;

  const openHostedReceipt = async () => {
    const url = (order as any).receipt_url as string | undefined;
    if (!url) return;
    try {
      await Linking.openURL(url);
    } catch (e: any) {
      Alert.alert("Error", e?.message || "Failed to open receipt");
    }
  };

  return (
    <ScrollView style={s.container} contentInsetAdjustmentBehavior="automatic">
      {/* Header */}
      <View style={s.header}>
        <Text style={s.title}>Receipt</Text>
        <Text style={s.subTitle}>{format(created, "MMM d, yyyy, h:mm a")}</Text>
      </View>

      {/* Merchant / Payment */}
      <View style={s.card}>
        <View style={s.rowBetween}>
          <Text style={s.muted}>Merchant</Text>
          <Text style={s.value}>JadeTrade</Text>
        </View>
        <View style={s.rowBetween}>
          <Text style={s.muted}>Payment method</Text>
          <Text style={s.value}>
            {(order.payment_method || "card").toUpperCase()}
            {brand ? ` · ${brand}` : ""}
            {last4 ? ` · •••• ${last4}` : ""}
          </Text>
        </View>
        <View style={s.rowBetween}>
          <Text style={s.muted}>Status</Text>
          <Text style={s.value}>{order.payment_status ?? "succeeded"}</Text>
        </View>
        {!!order.payment_intent_id && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Payment ID</Text>
            <Text style={s.value}>
              {String(order.payment_intent_id).slice(0, 12)}…
            </Text>
          </View>
        )}
      </View>

      {/* Items */}
      <View style={s.card}>
        <Text style={s.sectionTitle}>Items</Text>
        {lines.map((l, idx) => (
          <View key={`line-${idx}`} style={s.itemRow}>
            <Text style={[s.itemTitle, { flex: 1 }]} numberOfLines={2}>
              {l.title}
            </Text>
            <Text style={s.itemMeta}>
              {l.qty} × {money(l.price)}
            </Text>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={s.card}>
        <View style={s.rowBetween}>
          <Text style={s.muted}>Subtotal</Text>
          <Text style={s.value}>{money(subtotal)}</Text>
        </View>
        {typeof platformFee === "number" && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Processing Fee</Text>
            <Text style={s.value}>{money(platformFee)}</Text>
          </View>
        )}
        {typeof subtotal === "number" && typeof platformFee !== "number" && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Processing Fee</Text>
            <Text style={s.value}>{money(DeliveryHelpers.calculateBuyerFee(subtotal, delivery ?? 0))}</Text>
          </View>
        )}
        {typeof delivery === "number" && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Delivery</Text>
            <Text style={s.value}>{money(delivery)}</Text>
          </View>
        )}

        <View style={[s.rowBetween, { marginTop: 8 }]}>
          <Text style={s.totalLabel}>Total</Text>
          <Text style={s.totalValue}>{money(order.total_amount)}</Text>
        </View>
      </View>

      {/* Actions */}
      <View style={s.actions}>
        {(order as any).receipt_url ? (
          <TouchableOpacity
            style={[s.btn, s.btnPrimary]}
            onPress={openHostedReceipt}
          >
            <MaterialIcons name="open-in-new" size={18} color="#fff" />
            <Text style={s.btnPrimaryText}>Open Stripe Receipt</Text>
          </TouchableOpacity>
        ) : null}

        <TouchableOpacity
          style={[s.btn, s.btnSecondary]}
          onPress={() => navigation.goBack()}
        >
          <Feather name="arrow-left" size={18} color={colors.primary} />
          <Text style={s.btnSecondaryText}>Back</Text>
        </TouchableOpacity>
      </View>

      <View style={{ height: 24 }} />
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    center: { flex: 1, alignItems: "center", justifyContent: "center", gap: 8 },
    muted: { color: colors.textLight },
    header: { padding: spacing.md, paddingBottom: spacing.sm },
    title: { fontSize: 20, fontWeight: "800", color: colors.text },
    subTitle: { ...typography.caption, color: colors.textLight, marginTop: 2 },

    card: {
      backgroundColor: colors.surface,
      marginHorizontal: spacing.md,
      marginTop: spacing.sm,
      padding: spacing.md,
      borderRadius: 14,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 0.05,
      shadowRadius: 12,
      elevation: 2,
    },

    sectionTitle: {
      fontSize: 16,
      fontWeight: "800",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    rowBetween: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginVertical: 4,
    },
    value: { color: colors.text, fontWeight: "600" },

    itemRow: {
      flexDirection: "row",
      alignItems: "center",
      marginVertical: 6,
      gap: 8,
    },
    itemTitle: { fontSize: 15, color: colors.text, fontWeight: "600" },
    itemMeta: { fontSize: 13, color: colors.textLight },

    totalLabel: { fontSize: 16, color: colors.text, fontWeight: "800" },
    totalValue: { fontSize: 18, color: colors.text, fontWeight: "800" },

    actions: { marginHorizontal: spacing.md, marginTop: spacing.md },
    btn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      paddingVertical: 12,
      borderRadius: 12,
      marginTop: 10,
    },
    btnPrimary: { backgroundColor: colors.primary },
    btnPrimaryText: { color: "#fff", fontWeight: "800" },
    btnSecondary: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    btnSecondaryText: { color: colors.primary, fontWeight: "800" },
  });
